import React, { useEffect } from "react";
import { Modal } from "react-bootstrap";
import clsx from "clsx";

interface ModalNuevoUsuarioProps {
  formik: any;
  show: boolean;
  onClose: () => void;
  isEditing: boolean;
  loading: boolean;
}

export const ModalNuevoUsuario: React.FC<ModalNuevoUsuarioProps> = ({
  formik,
  show,
  onClose,
  isEditing,
  loading,
}) => {
  const allStatusOptions = [
    "Activo",
    "No Pagado",
    "Pago fallido",
    "Cancelado",
    "Prueba",
    "Demo",
    "Sistema",
  ];

  const filteredStatusOptions =
    formik.values.plan === "Admin"
      ? ["Sistema"]
      : allStatusOptions.filter((status) => status !== "Sistema");

  useEffect(() => {
    if (formik.values.plan !== "Admin" && formik.values.status === "Sistema") {
      formik.setFieldValue("status", "");
    }
  }, [formik.values.plan]);

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <form
        className="form w-100 fv-plugins-bootstrap5 fv-plugins-framework"
        noValidate
        onSubmit={formik.handleSubmit}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? "Editar usuario" : "Registrar usuario"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="card-body">
            {/* Nombre y Apellido */}
            <div className="row fv-row mb-7">
              {["firstname", "lastname"].map((field) => (
                <div className="col-xl-6" key={field}>
                  <label className="form-label fw-bolder text-dark fs-6 mb-0">
                    {field === "firstname" ? "Nombre*" : "Apellido*"}
                  </label>
                  <input
                    type="text"
                    placeholder={field === "firstname" ? "Nombre" : "Apellido"}
                    autoComplete="off"
                    {...formik.getFieldProps(field)}
                    className={clsx(
                      "form-control form-control-lg form-control-solid",
                      {
                        "is-invalid":
                          formik.touched[field] && formik.errors[field],
                      },
                      {
                        "is-valid":
                          formik.touched[field] && !formik.errors[field],
                      }
                    )}
                  />
                  {formik.touched[field] && formik.errors[field] && (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">
                        <span role="alert">{formik.errors[field]}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Teléfono y Email */}
            <div className="row fv-row mb-7">
              <div className="col-xl-6">
                <label className="form-label fw-bolder text-dark fs-6 mb-0">
                  Teléfono*
                </label>
                <input
                  type="text"
                  placeholder="Teléfono"
                  autoComplete="off"
                  {...formik.getFieldProps("phone")}
                  className={clsx(
                    "form-control form-control-lg form-control-solid",
                    {
                      "is-invalid":
                        formik.touched.phone && formik.errors.phone,
                    },
                    {
                      "is-valid":
                        formik.touched.phone && !formik.errors.phone,
                    }
                  )}
                />
                {formik.touched.phone && formik.errors.phone && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      <span role="alert">{formik.errors.phone}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="col-xl-6">
                <label className="form-label fw-bolder text-dark fs-6 mb-0">
                  Correo electrónico*
                </label>
                <input
                  type="email"
                  placeholder="Correo electrónico"
                  autoComplete="off"
                  {...formik.getFieldProps("email")}
                  className={clsx(
                    "form-control form-control-lg form-control-solid",
                    {
                      "is-invalid":
                        formik.touched.email && formik.errors.email,
                    },
                    {
                      "is-valid":
                        formik.touched.email && !formik.errors.email,
                    }
                  )}
                />
                {formik.touched.email && formik.errors.email && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      <span role="alert">{formik.errors.email}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Empresa, Empleados, Ciudad, Estado */}
            <div className="row fv-row mb-7">
              <div className="col-xl-6">
                <label className="form-label fw-bolder text-dark fs-6 mb-0">
                  Empresa*
                </label>
                <input
                  type="text"
                  placeholder="Empresa"
                  autoComplete="off"
                  {...formik.getFieldProps("company")}
                  className={clsx(
                    "form-control form-control-lg form-control-solid",
                    {
                      "is-invalid":
                        formik.touched.company && formik.errors.company,
                    },
                    {
                      "is-valid":
                        formik.touched.company && !formik.errors.company,
                    }
                  )}
                />
                {formik.touched.company && formik.errors.company && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      <span role="alert">{formik.errors.company}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="col-xl-6">
                <label className="form-label fw-bolder text-dark fs-6 mb-0">
                  Número de empleados*
                </label>
                <input
                  type="number"
                  placeholder="Número de empleados"
                  autoComplete="off"
                  {...formik.getFieldProps("employees")}
                  className={clsx(
                    "form-control form-control-lg form-control-solid",
                    {
                      "is-invalid":
                        formik.touched.employees && formik.errors.employees,
                    },
                    {
                      "is-valid":
                        formik.touched.employees && !formik.errors.employees,
                    }
                  )}
                />
                {formik.touched.employees && formik.errors.employees && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      <span role="alert">{formik.errors.employees}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="row fv-row mb-7">
              <div className="col-xl-6">
                <label className="form-label fw-bolder text-dark fs-6 mb-0">
                  Ciudad*
                </label>
                <input
                  type="text"
                  placeholder="Ciudad"
                  autoComplete="off"
                  {...formik.getFieldProps("city")}
                  className={clsx(
                    "form-control form-control-lg form-control-solid",
                    {
                      "is-invalid":
                        formik.touched.city && formik.errors.city,
                    },
                    {
                      "is-valid":
                        formik.touched.city && !formik.errors.city,
                    }
                  )}
                />
                {formik.touched.city && formik.errors.city && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      <span role="alert">{formik.errors.city}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="col-xl-6">
                <label className="form-label fw-bolder text-dark fs-6 mb-0">
                  Estado*
                </label>
                <input
                  type="text"
                  placeholder="Estado"
                  autoComplete="off"
                  {...formik.getFieldProps("state")}
                  className={clsx(
                    "form-control form-control-lg form-control-solid",
                    {
                      "is-invalid":
                        formik.touched.state && formik.errors.state,
                    },
                    {
                      "is-valid":
                        formik.touched.state && !formik.errors.state,
                    }
                  )}
                />
                {formik.touched.state && formik.errors.state && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      <span role="alert">{formik.errors.state}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Contraseña y Confirmación */}
            <div className="row fv-row mb-7">
              <div className="col-xl-6">
                <label className="form-label fw-bolder text-dark fs-6 mb-0">
                  Contraseña*
                </label>
                <input
                  type="password"
                  placeholder="Contraseña"
                  autoComplete="off"
                  {...formik.getFieldProps("password")}
                  className={clsx(
                    "form-control form-control-lg form-control-solid",
                    {
                      "is-invalid":
                        formik.touched.password && formik.errors.password,
                    },
                    {
                      "is-valid":
                        formik.touched.password && !formik.errors.password,
                    }
                  )}
                />
                {formik.touched.password && formik.errors.password && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      <span role="alert">{formik.errors.password}</span>
                    </div>
                  </div>
                )}
                <div className="text-muted">
                  Usa 8 o más caracteres con una mezcla de letras, números y
                  símbolos.
                </div>
              </div>

              <div className="col-xl-6">
                <label className="form-label fw-bolder text-dark fs-6 mb-0">
                  Confirmar contraseña*
                </label>
                <input
                  type="password"
                  placeholder="Confirmación de contraseña"
                  autoComplete="off"
                  {...formik.getFieldProps("changepassword")}
                  className={clsx(
                    "form-control form-control-lg form-control-solid",
                    {
                      "is-invalid":
                        formik.touched.changepassword &&
                        formik.errors.changepassword,
                    },
                    {
                      "is-valid":
                        formik.touched.changepassword &&
                        !formik.errors.changepassword,
                    }
                  )}
                />
                {formik.touched.changepassword &&
                  formik.errors.changepassword && (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">
                        <span role="alert">
                          {formik.errors.changepassword}
                        </span>
                      </div>
                    </div>
                  )}
              </div>
            </div>

            {/* Plan y Estatus */}
            <div className="row fv-row mb-7">
              <div className="col-xl-6">
                <label className="form-label fw-bolder text-dark fs-6 mb-0">
                  Plan*
                </label>
                <select
                  {...formik.getFieldProps("plan")}
                  className={clsx(
                    "form-control form-control-lg form-control-solid",
                    {
                      "is-invalid":
                        formik.touched.plan && formik.errors.plan,
                    },
                    {
                      "is-valid":
                        formik.touched.plan && !formik.errors.plan,
                    }
                  )}
                >
                  <option value="">Seleccione</option>
                  <option value="Admin">Admin</option>
                  <option value="Starter Mensual">Starter Mensual</option>
                  <option value="Professional Mensual">Professional Mensual</option>
                  <option value="Starter Anual">Starter Anual</option>
                  <option value="Professional Anual">Professional Anual</option>
                 
                </select>
                {formik.touched.plan && formik.errors.plan && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      <span role="alert">{formik.errors.plan}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="col-xl-6">
                <label className="form-label fw-bolder text-dark fs-6 mb-0">
                  Estatus*
                </label>
                <select
                  {...formik.getFieldProps("status")}
                  className={clsx(
                    "form-control form-control-lg form-control-solid",
                    {
                      "is-invalid":
                        formik.touched.status && formik.errors.status,
                    },
                    {
                      "is-valid":
                        formik.touched.status && !formik.errors.status,
                    }
                  )}
                >
                  <option value="">Seleccione</option>
                  {filteredStatusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
                {formik.touched.status && formik.errors.status && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      <span role="alert">{formik.errors.status}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Método de pago y Fecha */}
            <div className="row fv-row mb-7">
              <div className="col-xl-6">
                <label className="form-label fw-bolder text-dark fs-6 mb-0">
                  Método de pago*
                </label>
                <select
                  {...formik.getFieldProps("paymentMethod")}
                  className={clsx(
                    "form-control form-control-lg form-control-solid",
                    {
                      "is-invalid":
                        formik.touched.paymentMethod &&
                        formik.errors.paymentMethod,
                    },
                    {
                      "is-valid":
                        formik.touched.paymentMethod &&
                        !formik.errors.paymentMethod,
                    }
                  )}
                >
                  <option value="">Seleccione</option>
                  <option value="Stripe">Stripe</option>
                  <option value="Transferencia/Efectivo">
                    Transferencia/Efectivo
                  </option>
                  <option value="Por definir">Por definir</option>
                  <option value="Gratis">Gratis</option>
                </select>
                {formik.touched.paymentMethod &&
                  formik.errors.paymentMethod && (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">
                        <span role="alert">
                          {formik.errors.paymentMethod}
                        </span>
                      </div>
                    </div>
                  )}
              </div>

              <div className="col-xl-6">
                <label className="form-label fw-bolder text-dark fs-6 mb-0">
                  Fecha de pago*
                </label>
                <input
                  type="date"
                  {...formik.getFieldProps("paymentDate")}
                  className={clsx(
                    "form-control form-control-lg form-control-solid",
                    {
                      "is-invalid":
                        formik.touched.paymentDate &&
                        formik.errors.paymentDate,
                    },
                    {
                      "is-valid":
                        formik.touched.paymentDate &&
                        !formik.errors.paymentDate,
                    }
                  )}
                />
                {formik.touched.paymentDate && formik.errors.paymentDate && (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      <span role="alert">{formik.errors.paymentDate}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {formik.status && (
              <div className="mb-lg-15 alert alert-danger">
                <div className="alert-text font-weight-bold">
                  {formik.status}
                </div>
              </div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-end">
          <button
            type="button"
            className="btn btn-secondary btn-lg me-3"
            onClick={onClose}
          >
            Cerrar
          </button>
          <button
            type="submit"
            className="btn btn-primary btn-lg"
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {loading ? (
              <span className="indicator-progress" style={{ display: "block" }}>
                Por favor espere...{" "}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            ) : isEditing ? (
              "Actualizar usuario"
            ) : (
              "Registrar usuario"
            )}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};